import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { verifyQuoteText } from '../lib/api';
import { useDeleteQuote } from '../hooks/useQuotes';
import { useAuth } from '../context/AuthContext';
import type { Quote } from '../lib/api';

interface QuoteDetailProps {
  quote: Quote;
  onUpdate?: () => void;
}

export const QuoteDetail: React.FC<QuoteDetailProps> = ({ quote, onUpdate }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [selectedImage, setSelectedImage] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const deleteQuote = useDeleteQuote();

  const handleDelete = async () => {
    if (!confirm('Zitat wirklich löschen?')) return;
    deleteQuote.mutate(quote.id, {
      onSuccess: () => navigate('/'),
      onError: () => alert('Fehler beim Löschen'),
    });
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await verifyQuoteText(quote.id);
      onUpdate?.();
    } catch (error) {
      console.error('Fehler beim Verifizieren:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Bildergalerie */}
      {quote.images.length > 0 && (
        <div className="mb-8">
          {/* Hauptbild */}
          <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
            <img
              src={`/${quote.images[selectedImage].image_path}`}
              alt={`Zitat Bild ${selectedImage + 1}`}
              className="w-full h-full object-contain"
            />
            {quote.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                {selectedImage + 1} / {quote.images.length}
              </div>
            )}
            {quote.images[selectedImage].page_number && (
              <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                Seite {quote.images[selectedImage].page_number}
              </div>
            )}
          </div>

          {/* Thumbnail Navigation */}
          {quote.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {quote.images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                    index === selectedImage
                      ? 'border-blue-900'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={`/${image.thumbnail_path}`}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text Content */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Zitat #{quote.id}</h1>
          {isLoggedIn && (
            <div className="flex gap-2">
              <Link
                to={`/quotes/${quote.id}/edit`}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Bearbeiten
              </Link>
              <button
                onClick={handleDelete}
                disabled={deleteQuote.isPending}
                className="bg-red-100 text-red-700 px-4 py-2 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                {deleteQuote.isPending ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          )}
        </div>

        {quote.text_content ? (
          <div className="prose max-w-none">
            <blockquote className="text-lg text-gray-700 border-l-4 border-blue-900 pl-4 italic">
              {quote.text_content}
            </blockquote>
          </div>
        ) : (
          <p className="text-gray-500 italic">Kein Text verfügbar</p>
        )}

        {/* Status */}
        <div className="mt-4 flex items-center gap-4">
          {quote.text_status === 'ai_generated' ? (
            <div className="flex items-center gap-2">
              <span className="text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full text-sm">
                🤖 AI-generiert
              </span>
              {isLoggedIn && (
                <button
                  onClick={handleVerify}
                  disabled={isVerifying}
                  className="text-sm text-blue-900 underline hover:no-underline"
                >
                  {isVerifying ? 'Wird verifiziert...' : 'Als verifiziert markieren'}
                </button>
              )}
            </div>
          ) : (
            <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
              ✅ Manuell verifiziert
            </span>
          )}
        </div>
      </div>

      {/* Quelle */}
      {quote.source && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Quelle</h2>
          <Link
            to={`/sources/${quote.source.id}`}
            className="text-blue-900 hover:underline"
          >
            📖 {quote.source.title}
          </Link>
          {quote.source.year && (
            <span className="text-gray-500 ml-2">({quote.source.year})</span>
          )}
        </div>
      )}

      {/* Tags */}
      {quote.tags.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Tags</h2>
          <div className="flex flex-wrap gap-2">
            {quote.tags.map((tag) => (
              <Link
                key={tag.id}
                to={`/tags/${tag.id}`}
                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
